import React from 'react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { useMetaTracking } from '@/hooks/useMetaTracking';
interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent';
  children: React.ReactNode;
  to?: string; // Add optional 'to' prop for routing
}
const CTAButton = ({
  variant = 'primary',
  children,
  className,
  to,
  onClick,
  ...props
}: CTAButtonProps) => {
  const {
    trackLead,
    trackPurchase
  } = useMetaTracking();
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Call original onClick if provided
    if (onClick) {
      onClick(e);
    }

    // Track Meta conversion based on button text
    const buttonText = typeof children === 'string' ? children.toLowerCase() : '';
    if (buttonText.includes('free trial') || buttonText.includes('special offer') || buttonText.includes('claim')) {
      await trackLead({
        content_name: `CTA Button: ${buttonText}`
      });
    } else if (buttonText.includes('demo')) {
      await trackLead({
        content_name: `Demo Request: ${buttonText}`
      });
    }
  };
  const buttonContent = <button className={cn('relative py-3 px-5 sm:py-4 sm:px-6 md:py-6 md:px-12 lg:py-8 lg:px-16 rounded-full font-medium transition-all duration-300 overflow-hidden btn-hover-effect text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl min-h-[48px] md:min-h-[60px] lg:min-h-[72px] touch-manipulation', variant === 'primary' ? 'bg-[#003a70] text-white shadow-lg hover:shadow-xl hover:bg-opacity-90 active:scale-95 hover:scale-105' : variant === 'secondary' ? 'bg-transparent text-brand-dark border border-[#003a70]/30 hover:bg-[#003a70]/5 active:scale-95 hover:scale-105' : 'bg-[#FEE6D8] text-[#003a70] hover:bg-opacity-90 hover:shadow-md active:scale-95 hover:scale-105', className)} type="button" onClick={handleClick} {...props}>
      <span className="relative z-10 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl">{children}</span>
      <span className="absolute inset-0 w-full h-full bg-gradient-to-tr from-white/10 to-transparent opacity-0 hover:opacity-100 active:opacity-100 transition-opacity duration-300 pointer-events-none"></span>
      
    </button>;

  // Determine destination based on provided prop or button text
  let destination = to;
  if (!destination && typeof children === 'string') {
    if (children.toLowerCase().includes('free trial') || children.toLowerCase().includes('special offer') || children.toLowerCase().includes('claim')) {
      destination = 'https://buy.stripe.com/bIY17EaCh2FI0NicN5';
    } else if (children.toLowerCase().includes('demo')) {
      destination = '/book-demo';
    }
  } else if (!destination && React.isValidElement(children) && typeof children.props.children === 'string') {
    const text = children.props.children.toLowerCase();
    if (text.includes('free trial') || text.includes('special offer') || text.includes('claim')) {
      destination = 'https://buy.stripe.com/bIY17EaCh2FI0NicN5';
    } else if (text.includes('demo')) {
      destination = '/book-demo';
    }
  }

  // If a destination is determined or provided
  if (destination) {
    // External URL handling (starts with http:// or https://)
    if (destination.startsWith('http://') || destination.startsWith('https://')) {
      return <a href={destination} target="_blank" rel="noopener noreferrer" className="inline-block" onClick={e => e.stopPropagation()}>
          {buttonContent}
        </a>;
    }

    // Internal navigation with React Router's Link
    return <Link to={destination} className="inline-block" onClick={e => e.stopPropagation()}>
        {buttonContent}
      </Link>;
  }

  // Otherwise, just return the button
  return buttonContent;
};
export default CTAButton;